import { ClipboardList, CalendarCheck, FileCheck, Bell } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const BOARD_TYPE_VALUES = ["regular", "meeting", "evaluation", "reminder"] as const;
export type BoardType = (typeof BOARD_TYPE_VALUES)[number];

export interface BoardTypeLabels {
  entity: string;
  entityLower: string;
  singular: string;
  plural: string;
  dueDate: string;
  dueDateShort: string;
  noDueDate: string;
  assignee: string;
  assigneeShort: string;
  description: string;
  checklist: string;
  checklistItems: string;
  addItemPlaceholder: string;
  notFound: string;
  backToList: string;
  backToBoard: string;
  archiveTitle: string;
  archiveDescription: string;
  duplicateAction: string;
  unarchiveAction: string;
  searchPlaceholder: string;
  boardPickerTitle: string;
  addToExisting: string;
  titlePrefix: string;
  exportDateLabel: string;
  exportAssigneeLabel: string;
  exportDescriptionHeading: string;
  exportNotesHeading: string;
  contextPrompt: string;
}

export interface BoardTypeConfig {
  routePath: string;
  labels: BoardTypeLabels;
  icon: LucideIcon;
}

export const BOARD_TYPES: Record<string, BoardTypeConfig> = {
  regular: {
    routePath: "/tasks",
    icon: ClipboardList,
    labels: {
      entity: "Task",
      entityLower: "task",
      singular: "Task",
      plural: "Tasks",
      dueDate: "Due Date",
      dueDateShort: "Due:",
      noDueDate: "No due date",
      assignee: "Assigned",
      assigneeShort: "Assigned",
      description: "Description",
      checklist: "Checklist",
      checklistItems: "Checklist",
      addItemPlaceholder: "Add checklist item...",
      notFound: "Task not found",
      backToList: "Back to Tasks",
      backToBoard: "Back to Board",
      archiveTitle: "Archive this task?",
      archiveDescription: "This task will be hidden from the board. You can unarchive it later from the detail page.",
      duplicateAction: "Duplicate Task",
      unarchiveAction: "Unarchive Task",
      searchPlaceholder: "Search tasks...",
      boardPickerTitle: "Select a Task Board",
      addToExisting: "Add to Existing Task",
      titlePrefix: "",
      exportDateLabel: "Date",
      exportAssigneeLabel: "Assignee",
      exportDescriptionHeading: "Description",
      exportNotesHeading: "Notes",
      contextPrompt: "Any additional information about",
    },
  },
  meeting: {
    routePath: "/meetings",
    icon: CalendarCheck,
    labels: {
      entity: "Meeting Agenda",
      entityLower: "meeting agenda",
      singular: "Meeting",
      plural: "Meetings",
      dueDate: "Meeting Date",
      dueDateShort: "Meeting Date:",
      noDueDate: "No meeting date",
      assignee: "Meeting Lead",
      assigneeShort: "Lead",
      description: "Meeting Summary",
      checklist: "Agenda",
      checklistItems: "Agenda Items",
      addItemPlaceholder: "Add agenda item...",
      notFound: "Meeting agenda not found",
      backToList: "Back to Meetings",
      backToBoard: "Back to Meetings",
      archiveTitle: "Archive this meeting agenda?",
      archiveDescription: "This meeting agenda will be hidden from the board. You can unarchive it later from the detail page.",
      duplicateAction: "Duplicate Meeting Agenda",
      unarchiveAction: "Unarchive Meeting Agenda",
      searchPlaceholder: "Search meetings...",
      boardPickerTitle: "Select a Meeting Board",
      addToExisting: "Add to Existing Meeting",
      titlePrefix: "Meeting: ",
      exportDateLabel: "Meeting Date",
      exportAssigneeLabel: "Meeting Lead",
      exportDescriptionHeading: "Meeting Summary",
      exportNotesHeading: "Notes",
      contextPrompt: "What do you need to discuss about",
    },
  },
  evaluation: {
    routePath: "/evaluations",
    icon: FileCheck,
    labels: {
      entity: "Evaluation",
      entityLower: "evaluation",
      singular: "Evaluation",
      plural: "Evaluations",
      dueDate: "Due Date",
      dueDateShort: "Due:",
      noDueDate: "No due date",
      assignee: "Assigned",
      assigneeShort: "Assigned",
      description: "Description",
      checklist: "Checklist",
      checklistItems: "Checklist",
      addItemPlaceholder: "Add checklist item...",
      notFound: "Evaluation not found",
      backToList: "Back to Evaluations",
      backToBoard: "Back to Board",
      archiveTitle: "Archive this evaluation?",
      archiveDescription: "This evaluation will be hidden from the board. You can unarchive it later from the detail page.",
      duplicateAction: "Duplicate Evaluation",
      unarchiveAction: "Unarchive Evaluation",
      searchPlaceholder: "Search evaluations...",
      boardPickerTitle: "Select an Evaluation Board",
      addToExisting: "Add to Existing Evaluation",
      titlePrefix: "",
      exportDateLabel: "Date",
      exportAssigneeLabel: "Assignee",
      exportDescriptionHeading: "Description",
      exportNotesHeading: "Notes",
      contextPrompt: "Any additional information about",
    },
  },
  reminder: {
    routePath: "/dashboard",
    icon: Bell,
    labels: {
      entity: "Reminder",
      entityLower: "reminder",
      singular: "Reminder",
      plural: "Reminders",
      dueDate: "Remind Date",
      dueDateShort: "Remind:",
      noDueDate: "No remind date",
      assignee: "Owner",
      assigneeShort: "Owner",
      description: "Details",
      checklist: "Checklist",
      checklistItems: "Checklist",
      addItemPlaceholder: "Add checklist item...",
      notFound: "Reminder not found",
      backToList: "Back to Reminders",
      backToBoard: "Back to Board",
      archiveTitle: "Archive this reminder?",
      archiveDescription: "This reminder will be hidden from the board. You can unarchive it later from the detail page.",
      duplicateAction: "Duplicate Reminder",
      unarchiveAction: "Unarchive Reminder",
      searchPlaceholder: "Search reminders...",
      boardPickerTitle: "Select a Reminder Board",
      addToExisting: "Add to Existing Reminder",
      titlePrefix: "",
      exportDateLabel: "Remind Date",
      exportAssigneeLabel: "Owner",
      exportDescriptionHeading: "Details",
      exportNotesHeading: "Notes",
      contextPrompt: "What do you need to be reminded about",
    },
  },
};

export function getBoardTypeConfig(type?: string | null): BoardTypeConfig {
  return BOARD_TYPES[type || "regular"] || BOARD_TYPES.regular;
}

export function getBoardRoutePath(type?: string | null): string {
  return getBoardTypeConfig(type).routePath;
}

export function getBoardLabel<K extends keyof BoardTypeLabels>(
  type: string | undefined | null,
  key: K,
): string {
  return getBoardTypeConfig(type).labels[key];
}

export function getBoardEntityLabel(type?: string | null): string {
  return getBoardTypeConfig(type).labels.entity;
}

export function boardTypeFromRoute(routePath: string): string {
  for (const [type, config] of Object.entries(BOARD_TYPES)) {
    if (routePath.startsWith(config.routePath)) return type;
  }
  return "regular";
}

export function boardTypeFromContext(fromContext?: string | null): string {
  if (!fromContext) return "regular";
  return boardTypeFromRoute(`/${fromContext}`);
}
